/**
 * Geolocation and Haversine distance utilities
 */

const EARTH_RADIUS_METERS = 6371000;

export interface Coordinates {
  latitude: number;
  longitude: number;
  timestamp: number;
}

/**
 * Calculate great-circle distance between two points on Earth using Haversine formula
 * Returns distance in meters.
 */
export function calculateHaversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const toRad = (angle: number) => (angle * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
}

/**
 * Estimate speed in m/s from two consecutive GPS coordinate fixes.
 * If elapsed time is too small (< 100ms) or distance is negligible, returns 0.
 */
export function estimateSpeedFromCoordinates(
  prev: Coordinates,
  curr: Coordinates
): number {
  const dtSeconds = (curr.timestamp - prev.timestamp) / 1000;
  if (dtSeconds <= 0.1 || dtSeconds > 30) {
    // If timestamp difference is too small (< 100ms) or too large (> 30s gap), discard
    return 0;
  }

  const distanceMeters = calculateHaversineDistance(
    prev.latitude,
    prev.longitude,
    curr.latitude,
    curr.longitude
  );

  return distanceMeters / dtSeconds;
}
